<!-- Credit mgblaylock - https://github.com/ueberdosis/tiptap/issues/6025#issuecomment-3124747260 -->
<script>
  import { onMount, onDestroy } from 'svelte'
  import { Editor } from '@tiptap/core'
  import StarterKit from '@tiptap/starter-kit'
  import Link from '@tiptap/extension-link'
  import Blockquote from '@tiptap/extension-blockquote'
  import { cn } from '@lib/utils'
  import {
    Bold, Italic, Strikethrough,
    Heading2, Heading3, Heading4,
    List, ListOrdered,
    TextQuote, Code, Underline,
    Link2, Unlink2,
    Undo2, Redo2,
  } from 'lucide-svelte'

  let { value = $bindable(""), class: className, ...restProps } = $props()

  let element
  let editor = $state(null)

  onMount(() => {
    editor = new Editor({
      element: element,
      extensions: [
        StarterKit.configure({
          heading: {
            levels: [2, 3, 4],
          },
        }),
        Link.configure({
          openOnClick: false,
          HTMLAttributes: { class: 'underline underline-offset-2' },
        }),
        Blockquote.configure({
          HTMLAttributes: { class: 'border-l-2 border-gray-200 pl-4 py-1' },
        }),
      ],
      content: value,
      onSelectionUpdate: ({ editor: newEditor }) => {
        // force re-render so `editor.isActive` works as expected
        editor = undefined;
        editor = newEditor;
      },
      onUpdate: ({ editor: newEditor }) => {
        // force re-render so `editor.isActive` works as expected
        editor = undefined;
        editor = newEditor;
				value = newEditor.getHTML();
      },
      // Note: you can use `onTransaction` instead of `onSelectionUpdate` and `onUpdate` but it produces an error when using the browser back button.
    });
  })

  $effect(() => {
    // This makes sure if the parent passes in something new it sets Tiptap to use that instead of what it was.
    if (value !== editor?.getHTML()) {
      editor?.commands.setContent(value);
    }
  });

  function toggleLink() {
    if (editor.isActive('link')) {
      editor.chain().focus().unsetLink().run()
      return
    }
    const url = prompt('Enter URL:')
    if (url) {
      editor.chain().focus().setLink({ href: url }).run()
    }
  }

  onDestroy(() => {
    editor?.destroy()
  })

  const btn = "p-1.5 rounded text-gray-400 hover:bg-gray-100 hover:text-gray-600 transition-colors"
  const btnActive = "bg-gray-100 text-gray-700"
  const divider = "w-px h-5 bg-gray-200 mx-0.5"
</script>

<div class={cn("text-editor border border-gray-200 rounded-lg overflow-hidden focus-within:ring-2 focus-within:ring-primary-500/20 focus-within:border-primary-400 transition-colors", className)} {...restProps}>

  {#if editor}
    <div class="flex flex-wrap items-center gap-0.5 px-2 py-1.5 border-b border-gray-100 bg-gray-50/50">
      <button type="button" class="{btn} {editor.isActive('bold') ? btnActive : ''}" onclick={() => editor.chain().focus().toggleBold().run()} title="Bold">
        <Bold size={15} />
      </button>
      <button type="button" class="{btn} {editor.isActive('italic') ? btnActive : ''}" onclick={() => editor.chain().focus().toggleItalic().run()} title="Italic">
        <Italic size={15} />
      </button>
      <button type="button" class="{btn} {editor.isActive('underline') ? btnActive : ''}" onclick={() => editor.chain().focus().toggleUnderline().run()} title="Underline">
        <Underline size={15} />
      </button>
      <button type="button" class="{btn} {editor.isActive('strike') ? btnActive : ''}" onclick={() => editor.chain().focus().toggleStrike().run()} title="Strikethrough">
        <Strikethrough size={15} />
      </button>

      <div class={divider}></div>

      <button type="button" class="{btn} {editor.isActive('heading', { level: 2 }) ? btnActive : ''}" onclick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()} title="Heading 2">
        <Heading2 size={15} />
      </button>
      <button type="button" class="{btn} {editor.isActive('heading', { level: 3 }) ? btnActive : ''}" onclick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()} title="Heading 3">
        <Heading3 size={15} />
      </button>
      <button type="button" class="{btn} {editor.isActive('heading', { level: 4 }) ? btnActive : ''}" onclick={() => editor.chain().focus().toggleHeading({ level: 4 }).run()} title="Heading 4">
        <Heading4 size={15} />
      </button>

      <div class={divider}></div>

      <button type="button" class="{btn} {editor.isActive('bulletList') ? btnActive : ''}" onclick={() => editor.chain().focus().toggleBulletList().run()} title="Bullet list">
        <List size={15} />
      </button>
      <button type="button" class="{btn} {editor.isActive('orderedList') ? btnActive : ''}" onclick={() => editor.chain().focus().toggleOrderedList().run()} title="Ordered list">
        <ListOrdered size={15} />
      </button>

      <div class={divider}></div>

      <button type="button" class="{btn} {editor.isActive('blockquote') ? btnActive : ''}" onclick={() => editor.chain().focus().toggleBlockquote().run()} title="Blockquote">
        <TextQuote size={15} />
      </button>
      <button type="button" class="{btn} {editor.isActive('codeBlock') ? btnActive : ''}" onclick={() => editor.chain().focus().toggleCodeBlock().run()} title="Code block">
        <Code size={15} />
      </button>

      <div class={divider}></div>

      <button type="button" class="{btn} {editor.isActive('link') ? btnActive : ''}" onclick={toggleLink} title={editor.isActive('link') ? 'Remove link' : 'Add link'}>
        {#if editor.isActive('link')}
          <Unlink2 size={15} />
        {:else}
          <Link2 size={15} />
        {/if}
      </button>

      <div class="ml-auto"></div>

      <button type="button" class={btn} onclick={() => editor.chain().focus().undo().run()} title="Undo">
        <Undo2 size={15} />
      </button>
      <button type="button" class={btn} onclick={() => editor.chain().focus().redo().run()} title="Redo">
        <Redo2 size={15} />
      </button>
    </div>
  {/if}

  <div
    class="prose prose-sm max-w-none px-4 py-3 [&_.ProseMirror]:outline-none [&_.ProseMirror_:first-child]:mt-0 [&_.ProseMirror_:last-child]:mb-0 prose-headings:mt-4 prose-headings:mb-1 prose-p:my-1"
    bind:this={element}
  ></div>

</div>
